import { useEffect, useMemo, useState } from "react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Gauge from "@/components/Gauge";
import {
  KPI_FIELDS, MONTHS, MONTHS_F, YEARS,
  EMPTY_ENTRY, DEFAULT_OBJECTIVES,
  fmtNum, periodLabel, objMult,
} from "@/lib/kpi";

export default function AgentDashboard({ targetUser, onBack }) {
  const { user: authUser, logout } = useAuth();
  const user = targetUser || authUser;
  const isViewingOther = !!targetUser && targetUser.id !== authUser.id;
  const isDirector = authUser.role === "director";

  const [view, setView] = useState("mensuel");
  const [selYear, setSelYear] = useState(2026);
  const [selMonth, setSelMonth] = useState(new Date().getMonth());
  const [selQ, setSelQ] = useState(0);

  const [kpisByMonth, setKpisByMonth] = useState({}); // {"2026-5": {...}}
  const [objs, setObjs] = useState(DEFAULT_OBJECTIVES());
  const [entry, setEntry] = useState(EMPTY_ENTRY());
  const [saved, setSaved] = useState(false);

  const [showObj, setShowObj] = useState(false);
  const [objDraft, setObjDraft] = useState(DEFAULT_OBJECTIVES());

  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState("");
  const [aiErr, setAiErr] = useState("");

  // Load kpis + objectives for current user when user / year changes
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [kpiRes, objRes] = await Promise.all([
          api.get(`/users/${user.id}/kpis?year=${selYear}`),
          api.get(`/users/${user.id}/objectives`),
        ]);
        if (cancel) return;
        const map = {};
        kpiRes.data.forEach((k) => {
          map[`${k.year}-${k.month}`] = k;
        });
        setKpisByMonth(map);
        const o = {};
        KPI_FIELDS.forEach((f) => {
          o[f.key] = Number(objRes.data[f.key]) || 0;
        });
        setObjs(o);
      } catch (e) {
        console.error("Load failed", e);
      }
    })();
    return () => { cancel = true; };
  }, [user.id, selYear]);

  // Load entry for current month
  useEffect(() => {
    if (view !== "mensuel") return;
    const key = `${selYear}-${selMonth}`;
    const k = kpisByMonth[key];
    const e = EMPTY_ENTRY();
    if (k) {
      KPI_FIELDS.forEach((f) => {
        // Keep explicit 0 as "0" so the agent sees what was saved.
        e[f.key] = k[f.key] != null ? String(k[f.key]) : "";
      });
    }
    setEntry(e);
    setSaved(false);
  }, [view, selYear, selMonth, kpisByMonth]);

  const displayData = useMemo(() => {
    // aggregate according to view
    if (view === "mensuel") return entry;
    const keys = [];
    if (view === "trimestriel") {
      const b = selQ * 3;
      for (let i = 0; i < 3; i++) keys.push(`${selYear}-${b + i}`);
    } else {
      for (let i = 0; i < 12; i++) keys.push(`${selYear}-${i}`);
    }
    const agg = Object.fromEntries(KPI_FIELDS.map((f) => [f.key, 0]));
    let hasAnyMonth = false;
    keys.forEach((k) => {
      const d = kpisByMonth[k];
      if (!d) return;
      hasAnyMonth = true;
      KPI_FIELDS.forEach((f) => {
        agg[f.key] += Number(d[f.key]) || 0;
      });
    });
    // If no month was saved in this period, show "—" placeholders.
    // Otherwise keep real sums (including 0 — a legitimate "nothing happened").
    if (!hasAnyMonth) {
      return Object.fromEntries(KPI_FIELDS.map((f) => [f.key, ""]));
    }
    return agg;
  }, [view, entry, kpisByMonth, selYear, selQ]);

  const chartData = useMemo(() => {
    if (view === "mensuel") {
      return MONTHS.map((m, i) => {
        const d = kpisByMonth[`${selYear}-${i}`] || {};
        return {
          name: m,
          mandats: Number(d.mandats) || 0,
          compromis: Number(d.compromis) || 0,
          actes: Number(d.actes) || 0,
          ca: Number(d.ca) || 0,
        };
      });
    }
    if (view === "trimestriel") {
      return [0, 1, 2, 3].map((q) => {
        let mandats = 0, compromis = 0, actes = 0, ca = 0;
        for (const i of [q * 3, q * 3 + 1, q * 3 + 2]) {
          const d = kpisByMonth[`${selYear}-${i}`] || {};
          mandats += Number(d.mandats) || 0;
          compromis += Number(d.compromis) || 0;
          actes += Number(d.actes) || 0;
          ca += Number(d.ca) || 0;
        }
        return { name: `T${q + 1}`, mandats, compromis, actes, ca };
      });
    }
    return YEARS.map((y) => {
      const labels = MONTHS;
      return null;
    }).filter(Boolean) || [];
  }, [view, kpisByMonth, selYear]);

  // For annuel view, show all 12 months of selected year
  const annualChartData = useMemo(() => {
    return MONTHS.map((m, i) => {
      const d = kpisByMonth[`${selYear}-${i}`] || {};
      return {
        name: m,
        mandats: Number(d.mandats) || 0,
        compromis: Number(d.compromis) || 0,
        actes: Number(d.actes) || 0,
        ca: Number(d.ca) || 0,
      };
    });
  }, [kpisByMonth, selYear]);

  const effectiveChartData = view === "annuel" ? annualChartData : chartData;

  const mult = objMult(view);

  const saveEntry = async () => {
    try {
      const payload = { year: selYear, month: selMonth };
      KPI_FIELDS.forEach((f) => {
        payload[f.key] = Number(entry[f.key]) || 0;
      });
      const { data } = await api.put(`/users/${user.id}/kpis`, payload);
      setKpisByMonth((m) => ({ ...m, [`${selYear}-${selMonth}`]: data }));
      setSaved(true);
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  const openObj = () => {
    setObjDraft({ ...objs });
    setShowObj(true);
  };

  const saveObj = async () => {
    try {
      const payload = {};
      KPI_FIELDS.forEach((f) => (payload[f.key] = Number(objDraft[f.key]) || 0));
      const { data } = await api.put(`/users/${user.id}/objectives`, payload);
      const o = {};
      KPI_FIELDS.forEach((f) => (o[f.key] = Number(data[f.key]) || 0));
      setObjs(o);
      setShowObj(false);
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  const runAI = async () => {
    setAiErr("");
    setAiResult("");
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/analyze", {
        user_id: user.id,
        view,
        year: selYear,
        month: selMonth,
        quarter: selQ,
      });
      setAiResult(data.text || "");
    } catch (e) {
      setAiErr(formatApiError(e));
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div data-testid="agent-dashboard">
      <div className="hdr">
        <div>
          <div className="hdr-label">
            Performance individuelle · {user.agence || "Conseiller"}
          </div>
          <div className="hdr-title" data-testid="agent-name">{user.name}</div>
          {isViewingOther && (
            <div className="hdr-sub">Consulté par le directeur</div>
          )}
        </div>
        <div className="hdr-actions">
          {isViewingOther && isDirector && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={onBack}
              data-testid="back-to-team-btn"
            >
              ← Équipe
            </button>
          )}
          <button
            className="btn btn-ghost btn-sm"
            onClick={openObj}
            data-testid="open-objectives-btn"
          >
            {isViewingOther ? "Objectifs" : "Mes objectifs"}
          </button>
          {!isViewingOther && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={logout}
              data-testid="logout-btn"
            >
              Quitter
            </button>
          )}
        </div>
      </div>

      <div className="main">
        {/* View pills */}
        <div className="pills">
          {["mensuel", "trimestriel", "annuel"].map((v) => (
            <button
              key={v}
              className={`pill ${view === v ? "pill-active" : "pill-inactive"}`}
              onClick={() => { setView(v); setAiResult(""); }}
              data-testid={`view-pill-${v}`}
            >
              {v === "mensuel" ? "Mensuel" : v === "trimestriel" ? "Trimestriel" : "Annuel"}
            </button>
          ))}
        </div>

        {/* Period selectors */}
        <div className="period-bar">
          <select
            className="period-sel"
            value={selYear}
            onChange={(e) => setSelYear(Number(e.target.value))}
            data-testid="year-select"
          >
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          {view === "mensuel" && (
            <select
              className="period-sel"
              value={selMonth}
              onChange={(e) => { setSelMonth(Number(e.target.value)); setAiResult(""); }}
              data-testid="month-select"
            >
              {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
            </select>
          )}
          {view === "trimestriel" && (
            <select
              className="period-sel"
              value={selQ}
              onChange={(e) => { setSelQ(Number(e.target.value)); setAiResult(""); }}
              data-testid="quarter-select"
            >
              {[0, 1, 2, 3].map((q) => <option key={q} value={q}>T{q + 1}</option>)}
            </select>
          )}
          <div className="period-lbl">
            Période : <span>{periodLabel(view, selYear, selMonth, selQ)}</span>
          </div>
        </div>

        {/* KPI gauges */}
        <div className="grid-kpi">
          {KPI_FIELDS.map((f) => {
            const val = displayData[f.key];
            const o = objs[f.key] * mult;
            const display = val !== "" ? Number(val) : 0;
            return (
              <div
                key={f.key}
                className="card kpi-row"
                data-testid={`kpi-card-${f.key}`}
              >
                <Gauge value={display} objective={o} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="kpi-label">{f.label}</div>
                  <div className="kpi-val">
                    {val !== "" ? fmtNum(display, f.unit) : "—"}
                  </div>
                  <div className="kpi-obj">Obj. {fmtNum(o, f.unit)}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Saisie (monthly only) */}
        {view === "mensuel" && (
          <div className="card card-pad" style={{ marginBottom: 24 }}>
            <div className="sec-title">
              Saisie — {MONTHS[selMonth]} {selYear}
            </div>
            <div className="form-grid">
              {KPI_FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="form-label">{f.label}</label>
                  <input
                    className="form-input"
                    type="number"
                    min="0"
                    step="any"
                    value={entry[f.key]}
                    onChange={(e) => {
                      setEntry({ ...entry, [f.key]: e.target.value });
                      setSaved(false);
                    }}
                    data-testid={`entry-input-${f.key}`}
                  />
                </div>
              ))}
            </div>
            <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12 }}>
              <button
                className="btn btn-dark"
                onClick={saveEntry}
                data-testid="save-entry-btn"
              >
                Enregistrer
              </button>
              {saved && <span className="saved-badge">✓ Données enregistrées</span>}
            </div>
          </div>
        )}

        {/* Activity chart */}
        <div className="card card-pad" style={{ marginBottom: 24 }}>
          <div className="sec-title">Évolution — Activité</div>
          <div className="chart-wrap">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={effectiveChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#888" }} />
                <YAxis tick={{ fontSize: 11, fill: "#888" }} />
                <Tooltip
                  contentStyle={{
                    background: "#fff",
                    border: "1px solid #eee",
                    borderRadius: 6,
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="mandats" name="Mandats" fill="#c0392b" radius={[3, 3, 0, 0]} />
                <Bar dataKey="compromis" name="Compromis" fill="#2c3e50" radius={[3, 3, 0, 0]} />
                <Bar dataKey="actes" name="Actes" fill="#7f8c8d" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* CA chart */}
        <div className="card card-pad" style={{ marginBottom: 24 }}>
          <div className="sec-title">Évolution — CA HT (€)</div>
          <div className="chart-wrap-sm">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={effectiveChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#888" }} />
                <YAxis
                  tick={{ fontSize: 11, fill: "#888" }}
                  tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
                />
                <Tooltip
                  formatter={(v) => `${Number(v).toLocaleString("fr-FR")} €`}
                  contentStyle={{
                    background: "#fff",
                    border: "1px solid #eee",
                    borderRadius: 6,
                    fontSize: 12,
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="ca"
                  name="CA HT"
                  stroke="#c0392b"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: "#c0392b" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* AI analysis */}
        <div className="card card-pad">
          <div className="ai-header">
            <div className="sec-title" style={{ marginBottom: 0 }}>Analyse IA</div>
            <button
              className="btn btn-red"
              onClick={runAI}
              disabled={aiLoading}
              data-testid="run-ai-btn"
            >
              {aiLoading ? "Analyse en cours…" : "Générer l'analyse"}
            </button>
          </div>
          {aiErr && <div className="ai-result" style={{ color: "#c0392b" }}>{aiErr}</div>}
          {aiResult && (
            <div className="ai-result" data-testid="ai-result">{aiResult}</div>
          )}
        </div>
      </div>

      {/* Objectives modal */}
      {showObj && (
        <div
          className="overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowObj(false);
          }}
          data-testid="objectives-modal"
        >
          <div className="modal-box" style={{ maxWidth: 560 }}>
            <div className="sec-title">
              {isViewingOther ? `Objectifs — ${user.name}` : "Mes Objectifs Mensuels"}
            </div>
            <div className="form-grid">
              {KPI_FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="form-label">{f.label}</label>
                  <input
                    className="form-input"
                    type="number"
                    min="0"
                    step="any"
                    value={objDraft[f.key]}
                    onChange={(e) =>
                      setObjDraft({ ...objDraft, [f.key]: e.target.value })
                    }
                    data-testid={`obj-input-${f.key}`}
                  />
                </div>
              ))}
            </div>
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setShowObj(false)}>
                Annuler
              </button>
              <button
                className="btn btn-dark"
                onClick={saveObj}
                data-testid="save-objectives-btn"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
